import "./App.css";
import { useEffect, useState } from "react";
import {
  Mail,
  Github,
  Linkedin,
  MapPin,
  Calendar,
  ExternalLink,
  Award,
  Briefcase,
  GraduationCap,
  Globe,
  Code2,
  Send,
  Sun,
  Moon,
} from "lucide-react";

const THEME_STORAGE_KEY = "portfolio-theme";
const LANGUAGE_STORAGE_KEY = "portfolio-language";

const contentByLanguage = {
  en: {
    nav: {
      about: "About",
      skills: "Skills",
      experience: "Experience",
      education: "Education",
      projects: "Projects",
      contact: "Contact",
      themeDark: "Dark",
      themeLight: "Light",
      languageToggle: "DE",
    },
    hero: {
      badge: "Full-Stack Engineer",
      subtitlePrefix: "Building scalable applications at",
      educationLine:
        "M.Sc. International Information Systems - FAU Erlangen-Nürnberg",
      primaryButton: "Get in Touch",
      secondaryButton: "View Experience",
    },
    about: {
      label: "About Me",
      title: "Background & Languages",
      languageCardTitle: "Languages",
      paragraphs: [
        "I completed my Master's degree in International Information Systems at FAU Erlangen-Nürnberg, with a thesis focused on AI compliance under the EU AI Act. I now work as a Full-Stack Engineer at CONET Deutschland GmbH.",
        "My current focus is backend engineering, especially microservices and scalable platform architecture for a major automotive client in Munich. I am also getting deeper into cloud engineering with AWS and Terraform, including monitoring with Grafana and automated deployment workflows through CI/CD.",
        "Previously, I spent over 3 years at evosoft GmbH (a Siemens subsidiary), gaining valuable experience across enterprise IT projects including hospital navigation systems and sales process digitalization.",
      ],
    },
    skills: {
      label: "Tech Stack",
      title: "Skills & Technologies",
      categories: [
        {
          name: "Frontend",
          skills: [
            {
              name: "HTML/CSS",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg",
            },
            {
              name: "JavaScript",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg",
            },
            {
              name: "TypeScript",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg",
            },
            {
              name: "Angular",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg",
            },
            {
              name: "React",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
            },
            {
              name: "Tailwind CSS",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg",
            },
          ],
        },
        {
          name: "Backend",
          skills: [
            {
              name: "Java",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
            },
            {
              name: "Spring Boot",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg",
            },
            {
              name: "Spring",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg",
            },
            {
              name: "Python",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
            },
            {
              name: "Node.js",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
            },
            {
              name: "Kafka",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apachekafka/apachekafka-original.svg",
            },
            { name: "Microservices", icon: null },
          ],
        },
        {
          name: "Database & Cloud",
          skills: [
            {
              name: "SQL",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azuresqldatabase/azuresqldatabase-original.svg",
            },
            {
              name: "MongoDB",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
            },
            {
              name: "AWS",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
            },
            {
              name: "Docker",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
            },
            {
              name: "Terraform",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg",
            },
            { name: "Grafana", icon: null },
          ],
        },
        {
          name: "Tools & Methods",
          skills: [
            {
              name: "Git",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
            },
            {
              name: "Figma",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
            },
            { name: "REST API", icon: null },
            { name: "Agile/Scrum", icon: null },
            { name: "CI/CD", icon: null },
          ],
        },
      ],
    },
    experience: {
      label: "Experience",
      title: "Professional Journey",
      lengthLabel: "Length",
      currentBadge: "Current",
      entries: [
        {
          id: 1,
          title: "Fullstack Engineer (Backend Focus)",
          company: "CONET Deutschland GmbH",
          location: "Munich, Germany",
          duration: "May 2025 - Present",
          period: "Current",
          description:
            "As a Fullstack Engineer with a backend focus, I work on a project for a major automotive company in Munich.",
          tech: [
            "Angular",
            "TypeScript",
            "Java",
            "Python",
            "Spring",
            "Spring Boot",
            "Microservices",
            "AWS",
            "Terraform",
            "Grafana",
            "CI/CD",
          ],
          current: true,
        },
        {
          id: 2,
          title: "Working Student - Fullstack Software Development",
          company: "evosoft GmbH",
          location: "Nürnberg",
          duration: "March 2022 - March 2025",
          period: "2+ years",
          description:
            "Worked on several projects, including a hospital navigation tool and software for digitizing sales processes in collaboration with Siemens.",
          tech: [
            "Angular",
            "TypeScript",
            "Python",
            "SQL",
            "AWS Lambda",
            "API Gateway",
          ],
          current: false,
        },
        {
          id: 3,
          title: "Internship - Machine Learning Operations",
          company: "evosoft GmbH",
          location: "Nürnberg",
          duration: "October 2021 - March 2022",
          period: "6 months",
          description:
            "Worked in a research team focused on innovation and practical research in Data Science and MLOps.",
          tech: ["MLOps", "AWS S3", "SageMaker", "Terraform"],
          current: false,
        },
      ],
    },
    education: {
      label: "Education",
      title: "Academic Background",
      focusLabel: "Focus",
      thesisLabel: "Thesis",
      entries: [
        {
          id: 1,
          degree: "Master of Science",
          program: "International Information Systems",
          university: "FAU Erlangen-Nürnberg",
          duration: "Oct 2022 - Mar 2025",
          focus: "Data Engineering, Software Engineering, AI",
          thesis: "Development of an AI compliance tool based on the EU AI Act",
          logo: "FAU",
        },
        {
          id: 2,
          degree: "Semester Abroad",
          program: "Computer Science",
          university: "Riga Technical University",
          duration: "Jan 2024 - Jun 2024",
          focus: "AI, Applied Software Engineering, Testing",
          thesis: null,
          logo: "RTU",
        },
        {
          id: 3,
          degree: "Bachelor of Engineering",
          program: "Media Engineering",
          university: "TH Nürnberg",
          duration: "Oct 2018 - Sep 2022",
          focus: "Web Development, Android, HCI",
          thesis: "Automatic pattern recognition in financial instruments",
          logo: "THN",
        },
      ],
    },
    projects: {
      label: "Projects",
      title: "Featured Work",
      entries: [
        {
          id: 1,
          title: "Hospital Navigation Tool",
          description:
            "A comprehensive tool that helps hospital patients navigate inside complex buildings.",
          tech: ["Angular", "TypeScript", "Python", "AWS"],
          status: "nda",
          statusLabel: "NDA",
        },
        {
          id: 2,
          title: "Sales Process Digitalization",
          description:
            "Software developed with Siemens to digitalize and optimize sales processes.",
          tech: ["Python", "Machine Learning", "SQL", "AWS"],
          status: "nda",
          statusLabel: "NDA",
        },
        {
          id: 3,
          title: "Financial Pattern Recognition",
          description:
            "Bachelor thesis on automatic pattern recognition and detection in financial instruments.",
          tech: ["Python", "Machine Learning", "Data Analysis"],
          status: "thesis",
          statusLabel: "Thesis",
        },
        {
          id: 4,
          title: "AI Compliance Tool",
          description:
            "Master thesis on building an AI compliance tool aligned with the EU AI Act.",
          tech: ["AI", "Compliance", "EU AI Act"],
          status: "thesis",
          statusLabel: "Thesis",
        },
      ],
    },
    certifications: {
      label: "Certifications",
      title: "Credentials & Achievements",
      certificateCta: "View Certificate",
      entries: [
        {
          id: 1,
          title: "Enterprise Design Thinking Practitioner",
          issuer: "IBM",
          date: "Nov 2024",
          link: "https://www.credly.com/badges/ea2e445c-4848-4285-bda9-7979e7a43349/linked_in_profile",
        },
        {
          id: 2,
          title: "Angular (Intermediate)",
          issuer: "HackerRank",
          date: "2024",
          link: "https://www.hackerrank.com/certificates/d4c5253fee65",
        },
        {
          id: 3,
          title: "Software Engineer",
          issuer: "HackerRank",
          date: "2024",
          link: "https://www.hackerrank.com/certificates/iframe/37f8ba6d38e3",
        },
      ],
    },
    contact: {
      label: "Contact",
      title: "Let's Connect",
      subtitle:
        "Open to discussing new projects, opportunities, or simply having a conversation.",
      emailCta: "Get in Touch",
    },
    footer: "All rights reserved.",
    languagePills: [
      { flag: "\uD83C\uDDEB\uD83C\uDDF7", name: "French", level: "Native" },
      { flag: "\uD83C\uDDEC\uD83C\uDDE7", name: "English", level: "Fluent" },
      { flag: "\uD83C\uDDE9\uD83C\uDDEA", name: "German", level: "Fluent" },
      { flag: "\uD83C\uDDEA\uD83C\uDDF8", name: "Spanish", level: "B1" },
      { flag: "\uD83C\uDDF9\uD83C\uDDF3", name: "Arabic", level: "B1" },
    ],
  },
  de: {
    nav: {
      about: "Über mich",
      skills: "Skills",
      experience: "Erfahrung",
      education: "Ausbildung",
      projects: "Projekte",
      contact: "Kontakt",
      themeDark: "Dunkel",
      themeLight: "Hell",
      languageToggle: "EN",
    },
    hero: {
      badge: "Full-Stack-Engineer",
      subtitlePrefix: "Ich entwickle skalierbare Anwendungen bei",
      educationLine:
        "M.Sc. Internationale Informationssysteme - FAU Erlangen-Nürnberg",
      primaryButton: "Kontakt aufnehmen",
      secondaryButton: "Erfahrung ansehen",
    },
    about: {
      label: "Über mich",
      title: "Hintergrund & Sprachen",
      languageCardTitle: "Sprachen",
      paragraphs: [
        "Ich habe meinen Master in International Information Systems an der FAU Erlangen-Nürnberg abgeschlossen. In meiner Masterarbeit habe ich mich mit KI-Compliance im Kontext des EU AI Act beschäftigt. Heute arbeite ich als Full-Stack-Engineer bei CONET Deutschland GmbH.",
        "Mein aktueller Schwerpunkt liegt im Backend-Engineering, insbesondere bei Microservices und skalierbaren Plattformarchitekturen für einen großen Automobilkunden in München. Gleichzeitig vertiefe ich meine Cloud-Kompetenzen mit AWS und Terraform - inklusive Monitoring mit Grafana und automatisierten Deployment-Prozessen via CI/CD.",
        "Davor war ich über drei Jahre bei der evosoft GmbH (Siemens-Tochter) tätig und habe wertvolle Erfahrung in Enterprise-IT-Projekten gesammelt, unter anderem bei einem Krankenhaus-Navigationstool und bei der Digitalisierung von Vertriebsprozessen.",
      ],
    },
    skills: {
      label: "Tech Stack",
      title: "Technologien & Kompetenzen",
      categories: [
        {
          name: "Frontend",
          skills: [
            {
              name: "HTML/CSS",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg",
            },
            {
              name: "JavaScript",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg",
            },
            {
              name: "TypeScript",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg",
            },
            {
              name: "Angular",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg",
            },
            {
              name: "React",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
            },
            {
              name: "Tailwind CSS",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg",
            },
          ],
        },
        {
          name: "Backend",
          skills: [
            {
              name: "Java",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
            },
            {
              name: "Spring Boot",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg",
            },
            {
              name: "Spring",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg",
            },
            {
              name: "Python",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
            },
            {
              name: "Node.js",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
            },
            {
              name: "Kafka",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apachekafka/apachekafka-original.svg",
            },
            { name: "Microservices", icon: null },
          ],
        },
        {
          name: "Datenbanken & Cloud",
          skills: [
            {
              name: "SQL",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azuresqldatabase/azuresqldatabase-original.svg",
            },
            {
              name: "MongoDB",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
            },
            {
              name: "AWS",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
            },
            {
              name: "Docker",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
            },
            {
              name: "Terraform",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg",
            },
            { name: "Grafana", icon: null },
          ],
        },
        {
          name: "Tools & Methoden",
          skills: [
            {
              name: "Git",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
            },
            {
              name: "Figma",
              icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
            },
            { name: "REST API", icon: null },
            { name: "Agile/Scrum", icon: null },
            { name: "CI/CD", icon: null },
          ],
        },
      ],
    },
    experience: {
      label: "Erfahrung",
      title: "Mein beruflicher Weg",
      lengthLabel: "Dauer",
      currentBadge: "Aktuell",
      entries: [
        {
          id: 1,
          title: "Fullstack Engineer (Schwerpunkt Backend)",
          company: "CONET Deutschland GmbH",
          location: "München, Deutschland",
          duration: "Mai 2025 - heute",
          period: "Laufend",
          description:
            "Als Fullstack Engineer mit Backend-Schwerpunkt arbeite ich in einem Projekt für einen großen Automobilkunden in München.",
          tech: [
            "Angular",
            "TypeScript",
            "Java",
            "Python",
            "Spring",
            "Spring Boot",
            "Microservices",
            "AWS",
            "Terraform",
            "Grafana",
            "CI/CD",
          ],
          current: true,
        },
        {
          id: 2,
          title: "Werkstudent - Fullstack Softwareentwicklung",
          company: "evosoft GmbH",
          location: "Nürnberg",
          duration: "März 2022 - März 2025",
          period: "2+ Jahre",
          description:
            "Ich war an mehreren Projekten beteiligt, unter anderem an einem Krankenhaus-Navigationstool sowie an einer Software zur Digitalisierung von Vertriebsprozessen in Zusammenarbeit mit Siemens.",
          tech: [
            "Angular",
            "TypeScript",
            "Python",
            "SQL",
            "AWS Lambda",
            "API Gateway",
          ],
          current: false,
        },
        {
          id: 3,
          title: "Praktikum - Machine Learning Operations",
          company: "evosoft GmbH",
          location: "Nürnberg",
          duration: "Oktober 2021 - März 2022",
          period: "6 Monate",
          description:
            "Im Forschungsteam habe ich an innovativen Ansatzen im Bereich Data Science und MLOps mitgearbeitet.",
          tech: ["MLOps", "AWS S3", "SageMaker", "Terraform"],
          current: false,
        },
      ],
    },
    education: {
      label: "Ausbildung",
      title: "Akademischer Hintergrund",
      focusLabel: "Schwerpunkte",
      thesisLabel: "Abschlussarbeit",
      entries: [
        {
          id: 1,
          degree: "Master of Science",
          program: "International Information Systems",
          university: "FAU Erlangen-Nürnberg",
          duration: "Okt 2022 - Mär 2025",
          focus: "Data Engineering, Software Engineering, KI",
          thesis:
            "Entwicklung eines KI-Compliance-Tools auf Basis des EU AI Act",
          logo: "FAU",
        },
        {
          id: 2,
          degree: "Auslandssemester",
          program: "Informatik",
          university: "Riga Technical University",
          duration: "Jan 2024 - Jun 2024",
          focus: "KI, Applied Software Engineering, Testing",
          thesis: null,
          logo: "RTU",
        },
        {
          id: 3,
          degree: "Bachelor of Engineering",
          program: "Media Engineering",
          university: "TH Nürnberg",
          duration: "Okt 2018 - Sep 2022",
          focus: "Webentwicklung, Android, HCI",
          thesis: "Automatische Mustererkennung bei Finanzinstrumenten",
          logo: "THN",
        },
      ],
    },
    projects: {
      label: "Projekte",
      title: "Ausgewählte Arbeiten",
      entries: [
        {
          id: 1,
          title: "Krankenhaus-Navigationstool",
          description:
            "Ein umfassendes Tool, das Patientinnen und Patienten die Orientierung im Krankenhaus erleichtert.",
          tech: ["Angular", "TypeScript", "Python", "AWS"],
          status: "nda",
          statusLabel: "NDA",
        },
        {
          id: 2,
          title: "Digitalisierung von Vertriebsprozessen",
          description:
            "Softwarelösung in Zusammenarbeit mit Siemens zur Digitalisierung und Optimierung von Vertriebsprozessen.",
          tech: ["Python", "Machine Learning", "SQL", "AWS"],
          status: "nda",
          statusLabel: "NDA",
        },
        {
          id: 3,
          title: "Mustererkennung in Finanzdaten",
          description:
            "Bachelorarbeit zur automatischen Erkennung und Analyse von Mustern in Finanzinstrumenten.",
          tech: ["Python", "Machine Learning", "Data Analysis"],
          status: "thesis",
          statusLabel: "Abschlussarbeit",
        },
        {
          id: 4,
          title: "KI-Compliance-Tool",
          description:
            "Masterarbeit zur Entwicklung eines KI-Compliance-Tools entlang des EU AI Act.",
          tech: ["KI", "Compliance", "EU AI Act"],
          status: "thesis",
          statusLabel: "Abschlussarbeit",
        },
      ],
    },
    certifications: {
      label: "Zertifikate",
      title: "Zertifikate & Erfolge",
      certificateCta: "Zertifikat ansehen",
      entries: [
        {
          id: 1,
          title: "Enterprise Design Thinking Practitioner",
          issuer: "IBM",
          date: "Nov 2024",
          link: "https://www.credly.com/badges/ea2e445c-4848-4285-bda9-7979e7a43349/linked_in_profile",
        },
        {
          id: 2,
          title: "Angular (Intermediate)",
          issuer: "HackerRank",
          date: "2024",
          link: "https://www.hackerrank.com/certificates/d4c5253fee65",
        },
        {
          id: 3,
          title: "Software Engineer",
          issuer: "HackerRank",
          date: "2024",
          link: "https://www.hackerrank.com/certificates/iframe/37f8ba6d38e3",
        },
      ],
    },
    contact: {
      label: "Kontakt",
      title: "Lass uns vernetzen",
      subtitle:
        "Ich freue mich auf den Austausch zu neuen Projekten, spannenden Möglichkeiten oder einfach auf ein gutes Gespräch.",
      emailCta: "Kontakt aufnehmen",
    },
    footer: "Alle Rechte vorbehalten.",
    languagePills: [
      {
        flag: "\uD83C\uDDEB\uD83C\uDDF7",
        name: "Französisch",
        level: "Muttersprache",
      },
      {
        flag: "\uD83C\uDDEC\uD83C\uDDE7",
        name: "Englisch",
        level: "Fließend",
      },
      {
        flag: "\uD83C\uDDE9\uD83C\uDDEA",
        name: "Deutsch",
        level: "Fließend",
      },
      { flag: "\uD83C\uDDEA\uD83C\uDDF8", name: "Spanisch", level: "B1" },
      { flag: "\uD83C\uDDF9\uD83C\uDDF3", name: "Arabisch", level: "B1" },
    ],
  },
};

function Navigation({
  theme,
  locale,
  content,
  onToggleTheme,
  onToggleLanguage,
}) {
  return (
    <nav className="nav" data-testid="navigation">
      <div className="nav-inner">
        <a href="#" className="nav-logo" data-testid="nav-logo">
          <img
            src={`${process.env.PUBLIC_URL}/brand-mark.svg`}
            alt="Omar Fourati logo"
            className="nav-logo-mark"
          />
        </a>
        <ul className="nav-links">
          <li>
            <a href="#about" data-testid="nav-about">
              {content.nav.about}
            </a>
          </li>
          <li>
            <a href="#skills" data-testid="nav-skills">
              {content.nav.skills}
            </a>
          </li>
          <li>
            <a href="#experience" data-testid="nav-experience">
              {content.nav.experience}
            </a>
          </li>
          <li>
            <a href="#education" data-testid="nav-education">
              {content.nav.education}
            </a>
          </li>
          <li>
            <a href="#projects" data-testid="nav-projects">
              {content.nav.projects}
            </a>
          </li>
        </ul>
        <div className="nav-actions">
          <button
            type="button"
            className="theme-toggle"
            onClick={onToggleTheme}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            {theme === "dark" ? content.nav.themeLight : content.nav.themeDark}
          </button>
          <button
            type="button"
            className="lang-toggle"
            onClick={onToggleLanguage}
            aria-label={`Switch language to ${locale === "de" ? "English" : "Deutsch"}`}
            data-testid="language-toggle"
          >
            <Globe size={16} /> {content.nav.languageToggle}
          </button>
          <a
            href="#contact"
            className="nav-cta"
            data-testid="nav-contact-button"
          >
            <Send size={16} /> {content.nav.contact}
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero({ content }) {
  return (
    <section className="hero" data-testid="hero-section">
      <div className="container">
        <div className="hero-content">
          <span className="hero-badge">{content.hero.badge}</span>
          <h1 className="hero-title" data-testid="hero-name">
            Omar Fourati
          </h1>
          <p className="hero-subtitle">
            {content.hero.subtitlePrefix}{" "}
            <span className="hero-company">CONET Deutschland GmbH</span>
          </p>
          <p className="hero-education">
            <GraduationCap
              size={16}
              style={{
                display: "inline",
                verticalAlign: "middle",
                marginRight: 6,
              }}
            />
            {content.hero.educationLine}
          </p>
          <div className="hero-buttons">
            <a
              href="#contact"
              className="btn-primary"
              data-testid="contact-cta-button"
            >
              <Mail size={18} /> {content.hero.primaryButton}
            </a>
            <a
              href="#experience"
              className="btn-secondary"
              data-testid="experience-cta-button"
            >
              {content.hero.secondaryButton}
            </a>
          </div>
          <div className="hero-languages">
            {content.languagePills.map((languageItem, idx) => (
              <div key={idx} className="language-item">
                <span className="language-flag">{languageItem.flag}</span>
                <span>{languageItem.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function About({ content }) {
  return (
    <section
      id="about"
      className="section section-dark"
      data-testid="about-section"
    >
      <div className="container">
        <div className="section-header">
          <p className="section-label">{content.about.label}</p>
          <h2 className="section-title">{content.about.title}</h2>
        </div>
        <div className="about-grid">
          <div className="about-card">
            <div className="about-text">
              {content.about.paragraphs.map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </div>
          <div
            className="about-card languages-card"
            data-testid="languages-card"
          >
            <div className="languages-title">
              <Globe size={20} /> {content.about.languageCardTitle}
            </div>
            <div className="languages-list">
              {content.languagePills.map((languageItem, idx) => (
                <div key={idx} className="lang-item">
                  <span className="lang-flag">{languageItem.flag}</span>
                  <div className="lang-info">
                    <div className="lang-name">{languageItem.name}</div>
                    <div className="lang-level">{languageItem.level}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Skills({ content }) {
  return (
    <section
      id="skills"
      className="section section-alt"
      data-testid="skills-section"
    >
      <div className="container">
        <div className="section-header">
          <p className="section-label">{content.skills.label}</p>
          <h2 className="section-title">{content.skills.title}</h2>
        </div>
        {content.skills.categories.map((category, categoryIdx) => (
          <div key={categoryIdx} className="skills-category">
            <h3 className="skills-category-title">{category.name}</h3>
            <div className="skills-grid">
              {category.skills.map((skill, idx) => (
                <div
                  key={idx}
                  className="skill-card"
                  data-testid={`skill-${skill.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                >
                  {skill.icon ? (
                    <div className="skill-icon">
                      <img src={skill.icon} alt={skill.name} />
                    </div>
                  ) : (
                    <div className="skill-icon-placeholder">
                      <Code2 size={20} />
                    </div>
                  )}
                  <div className="skill-name">{skill.name}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Experience({ content }) {
  return (
    <section
      id="experience"
      className="section section-dark"
      data-testid="experience-section"
    >
      <div className="container">
        <div className="section-header">
          <p className="section-label">{content.experience.label}</p>
          <h2 className="section-title">{content.experience.title}</h2>
        </div>
        <div className="experience-list">
          {content.experience.entries.map((exp) => (
            <div
              key={exp.id}
              className={`experience-card ${exp.current ? "current" : ""}`}
              data-testid={`experience-card-${exp.id}`}
            >
              <div className="experience-header">
                <div>
                  <div className="experience-icon">
                    <Briefcase size={24} />
                  </div>
                  <h3 className="experience-title">{exp.title}</h3>
                  <p className="experience-company">{exp.company}</p>
                  <div className="experience-meta">
                    <span>
                      <MapPin size={14} /> {exp.location}
                    </span>
                    <span>
                      <Calendar size={14} /> {exp.duration}
                    </span>
                    <span className="experience-period">
                      {content.experience.lengthLabel}: {exp.period}
                    </span>
                  </div>
                </div>
                {exp.current && (
                  <span className="experience-badge">
                    {content.experience.currentBadge}
                  </span>
                )}
              </div>
              <p className="experience-description">{exp.description}</p>
              <div className="experience-tags">
                {exp.tech.map((tech, idx) => (
                  <span key={idx} className="tag">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Education({ content }) {
  return (
    <section
      id="education"
      className="section section-alt"
      data-testid="education-section"
    >
      <div className="container">
        <div className="section-header">
          <p className="section-label">{content.education.label}</p>
          <h2 className="section-title">{content.education.title}</h2>
        </div>
        <div className="education-grid">
          {content.education.entries.map((edu) => (
            <div
              key={edu.id}
              className="education-card"
              data-testid={`education-card-${edu.id}`}
            >
              <div className="education-logo">{edu.logo}</div>
              <h3 className="education-degree">{edu.degree}</h3>
              <p className="education-program">{edu.program}</p>
              <p className="education-university">{edu.university}</p>
              <p className="education-date">{edu.duration}</p>
              <p className="education-details">
                <strong>{content.education.focusLabel}:</strong> {edu.focus}
                {edu.thesis && (
                  <>
                    <br />
                    <strong>{content.education.thesisLabel}:</strong>{" "}
                    {edu.thesis}
                  </>
                )}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Projects({ content }) {
  return (
    <section
      id="projects"
      className="section section-dark"
      data-testid="projects-section"
    >
      <div className="container">
        <div className="section-header">
          <p className="section-label">{content.projects.label}</p>
          <h2 className="section-title">{content.projects.title}</h2>
        </div>
        <div className="projects-grid">
          {content.projects.entries.map((project) => (
            <div
              key={project.id}
              className="project-card"
              data-testid={`project-card-${project.id}`}
            >
              <span className={`project-status ${project.status}`}>
                {project.status === "nda" && "\uD83D\uDD12 "}
                {project.statusLabel}
              </span>
              <h3 className="project-title">{project.title}</h3>
              <p className="project-description">{project.description}</p>
              <div className="experience-tags">
                {project.tech.map((tech, idx) => (
                  <span key={idx} className="tag">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Certifications({ content }) {
  return (
    <section
      id="certifications"
      className="section section-alt"
      data-testid="certifications-section"
    >
      <div className="container">
        <div className="section-header">
          <p className="section-label">{content.certifications.label}</p>
          <h2 className="section-title">{content.certifications.title}</h2>
        </div>
        <div className="certifications-grid">
          {content.certifications.entries.map((cert) => (
            <div
              key={cert.id}
              className="certification-card"
              data-testid={`certification-card-${cert.id}`}
            >
              <div className="certification-logo">
                <Award size={22} />
              </div>
              <h3 className="certification-title">{cert.title}</h3>
              <p className="certification-issuer">{cert.issuer}</p>
              <p className="certification-date">{cert.date}</p>
              <a
                href={cert.link}
                target="_blank"
                rel="noopener noreferrer"
                className="certification-link"
                data-testid={`certification-link-${cert.id}`}
              >
                {content.certifications.certificateCta}{" "}
                <ExternalLink size={14} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact({ content }) {
  return (
    <section
      id="contact"
      className="section contact"
      data-testid="contact-section"
    >
      <div className="container">
        <div className="section-header">
          <p className="section-label">{content.contact.label}</p>
          <h2 className="section-title">{content.contact.title}</h2>
        </div>
        <p className="contact-subtitle">{content.contact.subtitle}</p>
        <a
          href="mailto:contact@omarfourati.com"
          className="contact-email"
          data-testid="email-cta-button"
        >
          <Mail size={20} /> {content.contact.emailCta}
        </a>
        <div className="contact-social">
          <a
            href="https://github.com/omarfourati"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            data-testid="github-link"
          >
            <Github size={22} />
          </a>
          <a
            href="https://linkedin.com/in/omarfourati"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            data-testid="linkedin-link"
          >
            <Linkedin size={22} />
          </a>
          <a
            href="mailto:contact@omarfourati.com"
            className="social-link"
            data-testid="email-link"
          >
            <Mail size={22} />
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer({ content }) {
  return (
    <footer className="footer">
      (c) {new Date().getFullYear()} Omar Fourati. {content.footer}
    </footer>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") {
      return "dark";
    }

    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === "dark" || storedTheme === "light") {
      return storedTheme;
    }

    return "dark";
  });

  const [locale, setLocale] = useState(() => {
    if (typeof window === "undefined") {
      return "en";
    }

    const storedLocale = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLocale === "de" || storedLocale === "en") {
      return storedLocale;
    }

    return "en";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", locale);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
  }, [locale]);

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  };

  const toggleLanguage = () => {
    setLocale((currentLocale) => (currentLocale === "de" ? "en" : "de"));
  };

  const content = contentByLanguage[locale];

  return (
    <div className="App" data-testid="app-container">
      <Navigation
        theme={theme}
        locale={locale}
        content={content}
        onToggleTheme={toggleTheme}
        onToggleLanguage={toggleLanguage}
      />
      <main>
        <Hero content={content} />
        <About content={content} />
        <Skills content={content} />
        <Experience content={content} />
        <Education content={content} />
        <Projects content={content} />
        <Certifications content={content} />
        <Contact content={content} />
      </main>
      <Footer content={content} />
    </div>
  );
}

export default App;
